import axios from 'axios';
import qs from 'querystringify';
/* eslint-disable react/jsx-no-bind */
import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  FormEvent,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import API_ROUTES from 'config/apiRoutes';
import Button from 'shared/components/Button';
import ErrorText from 'shared/components/ErrorText';
import LoadingContainer from 'shared/components/LoadingContainer';
import Modal from 'shared/components/Modal';
import { InputV2 } from 'shared/components/Styled';
import useModal from 'shared/hooks/useModal';
import useTranslation from 'shared/hooks/useTranslation';

import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

const ContactFormStyled = styled.form`
  display: flex;
  color: ${({ theme }) => theme.COLORS.WHITE};
  flex-direction: column;
  width: 100%;

  ${({ theme }) => theme.MEDIA.LG} {
    grid-column: 2 / 3;
    align-self: end;
  }
`;

const InputWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  height: 3.2rem;
  max-width: 70rem;
  width: 100%;

  ${({ theme }) => theme.MEDIA.LG} {
    height: 6.5rem;
  }
`;

type WrapperProps = {
  isLoading: boolean;
  isPricing?: boolean;
};

const ContactFormButton = styled(Button)<Pick<WrapperProps, 'isPricing'>>`
  background: ${({ isPricing, theme }) => (isPricing ? theme.COLORS.V2.SECONDARY : theme.COLORS.V2.SECONDARY500)};
  border-radius: 0 0.4rem 0.4rem 0;
  font-weight: 500;
  font-size: 1.1rem;
  line-height: 1.3rem;
  height: 100%;
  padding: 1rem 1.8rem;

  ${({ theme }) => theme.MEDIA.LG} {
    font-weight: 500;
    font-size: 2.1rem;
    line-height: 2.5rem;
  }

  &:disabled {
    &:hover,
    &:focus {
      background-color: ${({ theme }) => theme.COLORS.V2.SECONDARY500};
    }
  }
`;

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 6.4rem 3.4rem;
  max-width: 140rem;

  ${({ theme }) => theme.MEDIA.LG} {
    display: ${({ isPricing }) => (isPricing ? 'flex' : 'grid')};
    grid-template-columns: ${({ isPricing }) => (isPricing ? 'unset' : '1fr 1fr')};
    justify-items: start;
    margin: 0 auto;
    padding: 9rem 3.4rem;
    box-sizing: border-box;
  }
`;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: ${({ theme }) => theme.FONTS.MAIN};
  margin-bottom: 16px;

  h2 {
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 2.2rem;
    color: ${({ theme }) => theme.COLORS.WHITE};
    text-align: left;

    ${({ theme }) => theme.MEDIA.LG} {
      font-weight: 600;
      font-size: 3.2rem;
      line-height: 3.6rem;
    }
  }

  span {
    font-weight: 300;
    font-size: 1.1rem;
    line-height: 1.8rem;
    color: ${({ theme }) => theme.COLORS.WHITE};
    text-align: left;

    ${({ theme }) => theme.MEDIA.LG} {
      font-size: 2.2rem;
      line-height: 3.8rem;
    }
  }
`;

const Line = styled.div`
  background-color: white;
  height: 0.1rem;
  margin-top: 1rem;
  width: 20%;

  ${({ theme }) => theme.MEDIA.LG} {
    margin-top: 2.4rem;
  }
`;

const SpanSmall = styled.span`
  font-size: 0.9rem;
  font-weight: 300;
  margin-top: 8px;
  line-height: 1.5rem;

  ${({ theme }) => theme.MEDIA.LG} {
    font-size: 1.3rem;
    line-height: 1.9rem;
  }
`;

const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0 24px 0;
`;

const ThankYouText = styled.div`
  font-size: 2.1rem;
  text-align: center;
  font-weight: 600;
  line-height: 1.5;
  padding: 0;

  ${({ theme }) => theme.MEDIA.MD} {
    padding: 0 5.6rem;
  }
`;

const ImageWrapper = styled.div`
  height: 100%;
  position: absolute;
  width: 100%;
  top: 0;
  z-index: -1;
`;

async function sendContactRequest(emailOrPhone: string): Promise<number> {
  const data = qs.stringify({ contactData: emailOrPhone });
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
  };
  const res = await axios.post(API_ROUTES.CONTACT, data, { headers });
  return res.status;
}

interface ContactFormProps extends ComponentPropsWithoutRef<'div'> {
  backgroundSrc?: string | undefined;
  children: ReactNode;
  customPage?: string;
}

const ContactForm = forwardRef<HTMLDivElement, ContactFormProps>(function ContactForm(
  { backgroundSrc = 'shared/email-form.png', children, customPage = '' },
  ref,
) {
  const { t } = useTranslation('common');

  const router = useRouter();

  const [contactRequestSent, setContactRequestSent] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [isValid, setIsValid] = useState(true);
  const { current: phoneRegExp } = useRef(new RegExp('^[-+/s]*([0-9][-+/s]*){9,}$'));
  const { current: emailRegExp } = useRef(new RegExp(/\S+@\S+\.\S+/));
  const { getModalProps, isOpen, closeModal, openModal } = useModal();

  useEffect(() => {
    if (!isLoading && contactRequestSent && !isOpen) {
      closeModal();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isLoading, contactRequestSent, isOpen, closeModal]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!emailOrPhone || !(phoneRegExp.test(emailOrPhone) || emailRegExp.test(emailOrPhone))) {
      setIsValid(false);
      return;
    }
    try {
      setIsLoading(true);
      await sendContactRequest(emailOrPhone);
      setContactRequestSent(true);
      openModal();
    } catch (err) {
      setContactRequestSent(false);
    } finally {
      setIsLoading(false);
    }
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const { value } = e.target;
    if (!isValid) {
      setIsValid(true);
    }
    setEmailOrPhone(value);
  }

  return (
    <>
      <Head>
        {process.env.NEXT_PUBLIC_APP_ENV === 'prod' && contactRequestSent ? (
          <script>
            {`
              (() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'event',
                  eventCategory: 'button - contact request',
                  eventAction: 'click',
                  eventLabel: 'page - ${router.pathname}',
                });
              })();
            `}
          </script>
        ) : null}
      </Head>
      <StyledWrapper ref={ref}>
        <Modal {...getModalProps()}>
          <ModalBody>
            <ThankYouText>
              {t('contactForm.thankYourForMessage')} <br />
              {t('contactForm.weWillContactSoon')}
            </ThankYouText>
          </ModalBody>
          <Modal.CloseButton onClick={closeModal} />
        </Modal>
        {isLoading ? <LoadingContainer isLoading /> : null}
        <Wrapper isLoading={isLoading} isPricing={customPage === 'pricing'}>
          <StyledDiv>
            {children}
            {customPage === 'pricing' ? null : <Line />}
          </StyledDiv>
          <ContactFormStyled onSubmit={handleSubmit}>
            <InputWrapper>
              <InputV2
                placeholder={t('contactForm.emailOrPassword')}
                name="emailOrPhone"
                type="text"
                value={emailOrPhone}
                onChange={handleChange}
                error={!isValid || contactRequestSent === false}
              />
              <ContactFormButton type="submit" disabled={!isValid} isPricing={customPage === 'pricing'}>
                {t('contactForm.send')}
              </ContactFormButton>
            </InputWrapper>
            {!isValid ? <ErrorText>{t('contactForm.emailPhoneErrorMessage')}</ErrorText> : null}
            {contactRequestSent === null || contactRequestSent ? null : (
              <ErrorText>{t('contactForm.serverErrorMessage')}</ErrorText>
            )}
            <SpanSmall>{t('contactForm.confirmation')}</SpanSmall>
          </ContactFormStyled>
        </Wrapper>
        {customPage === 'pricing' ? null : (
          <ImageWrapper>
            <Image
              src={`/img/${backgroundSrc}`}
              alt=""
              layout="fill"
              objectFit="cover"
              quality="60"
              objectPosition="center top"
            />
          </ImageWrapper>
        )}
      </StyledWrapper>
    </>
  );
});

export default ContactForm;
